/**
 * A figure in a document: an `image` node holding an ATTACHMENT USID, never the bytes.
 *
 * WHY THE BODY HOLDS AN IDENTIFIER AND NOT THE PICTURE. Three independent reasons, each on its own
 * fatal, written out in cres_t11/service/document_image.py and worth repeating at the other end:
 *
 *   The body is a PAYLOAD -- it crosses a lambda, which carries 6MB. One inlined photograph is most
 *   of that; two is a document that cannot be saved.
 *
 *   The body is a CRDT -- live edits travel over AWS IoT, which caps a message at 128KB. An inlined
 *   image makes an update nobody else in the document ever receives: the picture exists in the tab
 *   that added it and in no other, and nothing anywhere reports an error.
 *
 *   The body is EVIDENCE -- a figure in a controlled document is part of the record, and the
 *   record's files already have retention, a sweep, and a visibility check. Bytes in a JSON column
 *   have none of those.
 *
 * SO `src` IS NOT AN ATTRIBUTE. It is resolved at render time from the URL map the server sends with
 * the body. Persisting it would be wrong twice: a presigned URL expires, and writing a fresh one in
 * would change the body -- which moves the ContentDigest and stales a pending approval on a document
 * nobody edited.
 *
 * WIDTH IS AN ATTRIBUTE, in CSS pixels, because the rendition reads it: a figure resized here is the
 * same size in the signed PDF. Height is never stored -- it follows from the file's own aspect ratio,
 * which is the only way a resize in one direction cannot stretch the picture.
 */
import { mergeAttributes, Node } from "@tiptap/core";

/**
 * Where the display URLs live.
 *
 * MODULE-LEVEL AND DELIBERATELY NOT IN THE DOCUMENT. Every node view reads this; the alternative --
 * each one fetching its own URL -- is one request per figure on every open, and the server already
 * returns them all with the body in a single round trip.
 */
const urls = new Map<string, string>();
const listeners = new Set<() => void>();

/** Remember the URLs that arrived with a body (or with a finished upload), and repaint. */
export function rememberFigures(images: Record<string, any> | undefined | null) {
    if (!images) return;
    let changed = false;
    for (const [attachment, value] of Object.entries(images)) {
        const url = typeof value === "string" ? value : value?.Url;
        if (url && urls.get(attachment) !== url) {
            urls.set(attachment, url);
            changed = true;
        }
    }
    if (changed) listeners.forEach(one => one());
}

/** Every attachment the open document refers to but has no URL for -- what a refresh must ask about. */
export function unresolvedFigures(body: any): string[] {
    const missing: string[] = [];
    const walk = (node: any) => {
        if (!node || typeof node !== "object") return;
        if (node.type === "image") {
            const attachment = node.attrs?.attachment;
            if (attachment && !urls.has(attachment) && !missing.includes(attachment)) {
                missing.push(attachment);
            }
        }
        (node.content || []).forEach(walk);
    };
    walk(body);
    return missing;
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        documentFigure: {
            insertFigure: (attrs: {attachment: string; alt?: string; width?: number}) => ReturnType;
            setFigureWidth: (width: number | null) => ReturnType;
        };
    }
}

export const DocumentFigure = Node.create({
    name: "image",
    group: "block",
    // ATOMIC: a figure has no editable content, so the cursor steps over it rather than into it and
    // a selection either holds the whole picture or none of it.
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return {
            attachment: {
                default: null,
                parseHTML: element => element.getAttribute("data-attachment"),
                renderHTML: attributes =>
                    attributes.attachment ? {"data-attachment": attributes.attachment} : {},
            },
            alt: {
                default: null,
                parseHTML: element => element.getAttribute("alt"),
                renderHTML: attributes => (attributes.alt ? {alt: attributes.alt} : {}),
            },
            // WHERE THE PICTURE SITS AND WHAT THE TEXT DOES AROUND IT. Named after where the
            // IMAGE goes, which is what somebody picks -- "wrap left" means the figure is on the
            // left and the words run down its right-hand side.
            //
            // "none" IS IN LINE WITH THE TEXT, which is what every figure in every document was
            // before this existed and must go on being. A default of anything else would reflow
            // every procedure in the tenant on the day it shipped.
            wrap: {
                default: "none",
                parseHTML: element => {
                    const held = element.getAttribute("data-wrap");
                    return held === "left" || held === "right" ? held : "none";
                },
                renderHTML: attributes => (attributes.wrap && attributes.wrap !== "none"
                    ? { "data-wrap": attributes.wrap } : {}),
            },
            width: {
                default: null,
                parseHTML: element => {
                    const value = element.getAttribute("width") || (element as HTMLElement).style?.width;
                    const parsed = parseInt(String(value || ""), 10);
                    return Number.isFinite(parsed) ? parsed : null;
                },
                renderHTML: attributes => (attributes.width ? {width: attributes.width} : {}),
            },
        };
    },

    // PARSED FROM data-attachment ONLY. An <img src="..."> pasted from a web page is deliberately
    // NOT a figure: we have no copy of those bytes, so it would render today and be a broken image
    // in the signed rendition forever. Dropping it is the honest outcome.
    parseHTML() {
        return [{tag: "img[data-attachment]"}];
    },

    renderHTML({HTMLAttributes}) {
        return ["img", mergeAttributes(HTMLAttributes, {class: "parkour-figure"})];
    },

    addCommands() {
        return {
            insertFigure: attrs => ({commands}) => commands.insertContent({type: this.name, attrs}),
            setFigureWidth: width => ({commands}) => commands.updateAttributes(this.name, {width}),
        };
    },

    /**
     * The node view exists for two things the schema cannot do: resolve `src` from the URL map, and
     * offer a drag handle to resize.
     *
     * PLAIN DOM, NOT REACT. It is one <img> and one handle; a React node view would bring a renderer
     * per figure for no gain.
     */
    addNodeView() {
        return ({node, editor, getPos}) => {
            const dom = document.createElement("div");
            dom.className = "parkour-figure-wrap";
            dom.setAttribute("data-attachment", node.attrs.attachment || "");

            const img = document.createElement("img");
            img.className = "parkour-figure";
            img.draggable = false;
            if (node.attrs.alt) img.alt = node.attrs.alt;

            // A PLACEHOLDER UNTIL THE URL ARRIVES, so a document whose figures are still resolving
            // does not reflow as each one lands.
            const pending = document.createElement("div");
            pending.className = "parkour-figure-pending";
            pending.textContent = "Loading figure…";

            const handle = document.createElement("span");
            handle.className = "parkour-figure-handle";
            handle.title = "Drag to resize";

            dom.appendChild(pending);
            dom.appendChild(img);
            dom.appendChild(handle);

            let current = node;

            const paint = () => {
                const attachment = current.attrs.attachment;
                const url = attachment ? urls.get(attachment) : undefined;
                if (url) {
                    if (img.getAttribute("src") !== url) img.setAttribute("src", url);
                    pending.hidden = true;
                    img.hidden = false;
                } else {
                    // UNRESOLVED IS NOT BROKEN. An attachment whose URL has not arrived yet, or whose
                    // hour expired, shows the placeholder rather than a broken-image glyph.
                    img.hidden = true;
                    pending.hidden = false;
                }
                img.style.width = current.attrs.width ? `${current.attrs.width}px` : "";
                // THE WRAP IS ON THE WRAPPER, NOT THE IMAGE, because what floats has to be the box
                // that carries the resize handle too -- floating the <img> alone leaves the handle
                // behind in the flow, pointing at nothing.
                const wrap = current.attrs.wrap === "left" || current.attrs.wrap === "right"
                    ? current.attrs.wrap : "none";
                dom.setAttribute("data-wrap", wrap);
                handle.hidden = !editor.isEditable;
            };

            const unsubscribe = subscribe(paint);
            paint();

            // RESIZING WRITES THE ATTRIBUTE, ONCE, ON RELEASE. Writing it on every pointermove would
            // put a hundred steps in the undo history and a hundred Yjs updates on the wire for one
            // drag.
            let dragging = false;
            let startX = 0;
            let startWidth = 0;

            handle.addEventListener("pointerdown", event => {
                if (!editor.isEditable) return;
                event.preventDefault();
                event.stopPropagation();
                dragging = true;
                startX = event.clientX;
                startWidth = img.getBoundingClientRect().width;
                handle.setPointerCapture(event.pointerId);
            });

            handle.addEventListener("pointermove", event => {
                if (!dragging) return;
                // CLAMPED TO THE TEXT COLUMN. A figure wider than the page cannot be drawn by the
                // rendition, which would turn a drag into a document that will not render.
                const limit = (dom.parentElement?.getBoundingClientRect().width) || 624;
                const next = Math.max(48, Math.min(limit, startWidth + (event.clientX - startX)));
                img.style.width = `${Math.round(next)}px`;
            });

            const finish = (event: PointerEvent) => {
                if (!dragging) return;
                dragging = false;
                try { handle.releasePointerCapture(event.pointerId); } catch { /* already released */ }
                const width = Math.round(img.getBoundingClientRect().width);
                const position = typeof getPos === "function" ? getPos() : null;
                if (position === null || position === undefined) return;
                editor.view.dispatch(
                    editor.view.state.tr.setNodeMarkup(position, undefined,
                        {...current.attrs, width}));
            };

            handle.addEventListener("pointerup", finish);
            handle.addEventListener("pointercancel", finish);

            return {
                dom,
                update(updated) {
                    if (updated.type.name !== "image") return false;
                    current = updated;
                    dom.setAttribute("data-attachment", updated.attrs.attachment || "");
                    if (updated.attrs.alt) img.alt = updated.attrs.alt;
                    paint();
                    return true;
                },
                selectNode() { dom.classList.add("parkour-figure-selected"); },
                deselectNode() { dom.classList.remove("parkour-figure-selected"); },
                // THE HANDLE IS NOT THE DOCUMENT. Without this ProseMirror treats a pointerdown on
                // it as a click in the text and collapses the selection, which cancels the drag.
                stopEvent: (event: Event) => event.target === handle,
                ignoreMutation: () => true,
                destroy() { unsubscribe(); },
            };
        };
    },
});

export default DocumentFigure;
